import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const EmergencyCareCoursePage: React.FC = () => {
  const emergencyCareData = {
    title: "Bachelor of Science in Accident and Emergency Care Technology",
    shortName: "B.Sc Emergency Care",
    duration: "3 Years",
    description: "A specialized program focusing on emergency medical care, trauma management, and critical care technologies for life-saving interventions.",
    overview: "B.Sc Accident and Emergency Care Technology prepares students to become skilled emergency medical technicians and paramedics. The program covers emergency medicine, trauma care, critical care procedures, ambulance operations, and disaster management. Students learn to provide immediate medical care in emergency situations, operate life-support equipment, and work effectively in high-pressure environments.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years", 
      "Medical fitness certificate required",
      "Good physical fitness and stamina",
      "Ability to work under extreme pressure"
    ],
    careerProspects: {
      roles: [
        "Emergency Medical Technician",
        "Paramedic",
        "Trauma Care Specialist", 
        "Emergency Room Technician",
        "Ambulance Technician",
        "Disaster Response Coordinator",
        "Critical Care Technician",
        "Emergency Services Supervisor",
        "Flight Paramedic",
        "Emergency Training Instructor"
      ],
      industries: [
        "Emergency Medical Services",
        "Hospitals and Trauma Centers",
        "Ambulance Services",
        "Disaster Management Agencies", 
        "Fire and Rescue Services",
        "Military Medical Services",
        "Industrial Safety Departments",
        "Sports Medicine Centers",
        "Aviation Medical Services",
        "Emergency Training Institutes"
      ],
      averageSalary: "₹3-8 LPA (varies by experience and specialization)",
      topRecruiters: [
        "AIIMS Trauma Center",
        "Apollo Emergency Services",
        "Fortis Emergency Care",
        "Max Emergency Medicine",
        "Government Emergency Services",
        "Indian Armed Forces",
        "State Disaster Management",
        "Private Ambulance Services",
        "Industrial Emergency Response",
        "Aviation Emergency Services"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology",
          "Basic Life Support",
          "Medical Terminology", 
          "Emergency Medicine Basics",
          "Communication Skills",
          "Physical Fitness Training",
          "Healthcare Systems"
        ]
      },
      {
        year: 2,
        subjects: [
          "Advanced Life Support",
          "Trauma Care",
          "Emergency Pharmacology",
          "Ambulance Operations",
          "Critical Care Procedures", 
          "Disaster Management",
          "Infection Control",
          "Emergency Equipment"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Emergency Care",
          "Pediatric Emergency Care",
          "Cardiac Emergency Management",
          "Mass Casualty Incidents",
          "Emergency Transportation",
          "Clinical Training",
          "Research in Emergency Care", 
          "Professional Ethics"
        ]
      }
    ],
    admissionProcess: [
      "Apply to medical colleges offering B.Sc Emergency Care",
      "Merit-based selection or entrance examination",
      "Physical fitness assessment",
      "Medical and psychological evaluation",
      "Document verification", 
      "Fee payment and admission",
      "Emergency response orientation",
      "Begin theoretical and practical training"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹8,000 PA",
        ranking: "Premier Government Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu", 
        fees: "₹2 LPA",
        ranking: "Top Private Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹3.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Medical College",
        location: "Mysore, Karnataka",
        fees: "₹3 LPA",
        ranking: "Well Established"
      },
      {
        name: "Government Medical College",
        location: "Kozhikode, Kerala",
        fees: "₹15,000 PA", 
        ranking: "Government College"
      }
    ],
    skills: [
      "Emergency Response",
      "Life Support Techniques",
      "Trauma Assessment",
      "Critical Thinking",
      "Quick Decision Making",
      "Equipment Operation",
      "Physical Stamina", 
      "Stress Management",
      "Team Coordination",
      "Communication Skills",
      "Compassionate Care",
      "Continuous Learning"
    ],
    futureScope: "Emergency care technology is evolving with telemedicine integration, advanced monitoring systems, and AI-assisted diagnostics. Future opportunities include specialization in helicopter emergency services, disaster response technology, and advanced trauma care. The growing focus on emergency preparedness and rapid response systems is creating new career paths in emergency management and critical care technology.",
    faqs: [
      {
        question: "What are the physical demands of emergency care work?",
        answer: "Emergency care work is physically demanding, requiring good fitness, ability to lift patients, work long hours, and respond quickly in stressful situations. Regular fitness training is part of the curriculum and career requirements."
      },
      {
        question: "How do you handle the emotional stress of emergency situations?", 
        answer: "Training includes stress management, psychological preparation, and counseling techniques. Professional support systems and regular debriefing help manage the emotional challenges of emergency care work."
      },
      {
        question: "Can I work in different emergency services after graduation?",
        answer: "Yes, graduates can work in various emergency services including hospital emergency departments, ambulance services, disaster response teams, industrial emergency response, and specialized rescue services."
      },
      {
        question: "Is there scope for career advancement?",
        answer: "Career advancement includes supervisory roles, specialized emergency services, training and education positions, emergency management coordination, and advanced certifications in specific emergency care areas."
      },
      {
        question: "What are the shift patterns like?", 
        answer: "Emergency services operate 24/7, so shift work including nights, weekends, and holidays is common. This provides variety but requires adaptation to irregular schedules."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {emergencyCareData.title} ({emergencyCareData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={emergencyCareData} />
      </div>
    </div>
  );
};

export default EmergencyCareCoursePage;